import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import {IonCol, IonLabel, IonLoading, IonRow} from '@ionic/react';
import {useTranslation} from 'react-i18next';
import {Controller, useFieldArray, useFormContext} from "react-hook-form";
import {StyledButtonOption} from '../../travelAllowance.style';
import {StyledInputGroup, StyledInputUnit} from "@components/form/input/input.style";
import {SubRoute, SubRouteFormField} from "@models/travelAllowance/rideRoute";
import {ReactComponent as AddIcon} from "@assets/images/travelAllowance/add.svg"
import {ReactComponent as BinIcon} from "@assets/images/travelAllowance/bin.svg"
import CityAutocomplete from "@components/google/cityAutocomplete/cityAutocomplete.component";
import {getFieldNameWithPrefix} from "@services/travelAllowance/travelAllowance";

type SubRoutesFormFieldProps = {
    prefix?: 'ride.route' | 'backRide.route',
    disabled?: boolean,
    onRowAdd?: () => void,
    onRowRemove?: () => void,
    onRowChange?: () => void,
}

export type SubRoutesFormFieldHandle = {
    setRows: (subRoutes: SubRoute[]|SubRouteFormField[]) => void;
    removeRows: () => void;
};

const SubRoutesFormField = forwardRef<SubRoutesFormFieldHandle, SubRoutesFormFieldProps>(({
                                                                                              prefix,
                                                                                              disabled,
                                                                                              onRowAdd,
                                                                                              onRowRemove,
                                                                                              onRowChange,
                                                                                          }, ref) => {
    const {t} = useTranslation();

    const {control, setValue, errors} = useFormContext();
    const {fields, append, remove, insert} = useFieldArray<SubRouteFormField>({
        control: control,
        name: prefix ? `${prefix}.subRoutes` : 'subRoutes',
    });

    const [showLoader, setShowLoader] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
        setRows(subRoutes: SubRoute[]|SubRouteFormField[]) {
            remove();

            subRoutes.forEach((item: SubRoute|SubRouteFormField, index: number) => {
                insert(index, {
                    startingPlace: item.startingPlace,
                    startingPlaceCountry: item.startingPlaceCountry,
                    destinationPlace: item.destinationPlace,
                    destinationPlaceCountry: item.destinationPlaceCountry,
                    distance: item.distance,
                    visible: index !== subRoutes.length - 1,
                });
            });
        },
        removeRows() {
            remove();
        }
    }), [append, remove]);

    useEffect(() => {
        if (fields.length === 1 && !fields[0].visible) {
            remove(0);
        }
    }, [fields]);

    const handleSubRouteAddNewRow = () => {
        if (fields.length === 0) {
            append({startingPlace: '', destinationPlace: '', startingPlaceCountry: '', destinationPlaceCountry: '', distance: 0, visible: true});
            append({startingPlace: '', destinationPlace: '', startingPlaceCountry: '', destinationPlaceCountry: '', distance: 0, visible: false});

            return;
        }

        const indexOfInvisible = fields.findIndex(subRoute => subRoute.visible === false);
        insert(indexOfInvisible, {startingPlace: '', destinationPlace: '', startingPlaceCountry: '', destinationPlaceCountry: '', distance: 0, visible: true});
        setValue(getFieldNameWithPrefix('distance', prefix), undefined, {shouldValidate: true});

        if (onRowAdd) {
            onRowAdd();
        }
    }

    const handleSubRouteDelete = (index: number) => {
        remove(index);

        setTimeout(() => {
            if (onRowRemove) {
                onRowRemove();
            }
        }, 500)
    }

    const getNestedValue = (obj: Record<string, any>, path: string): any => path.split('.').reduce((acc, key) => acc?.[key], obj);

    return (
        <section>
            <IonLoading onDidDismiss={() => setShowLoader(false)} isOpen={showLoader}/>
            {
                (() => {
                    let visibleIndex = 0;

                    return fields.map((subRoute, index) => {
                        if (subRoute.visible !== false) {
                            visibleIndex++;

                            return (
                                <div key={subRoute.id}>
                                    <IonRow className="mt-8">
                                        <IonCol size="12">
                                            <IonLabel>
                                                {t('travelAllowance.rideRoute.subRoute')} {visibleIndex}
                                            </IonLabel>
                                        </IonCol>
                                    </IonRow>
                                    <StyledInputGroup
                                        className={prefix ? (getNestedValue(errors, `${prefix}.subRoutes.${index}`) ? 'error' : '') : (getNestedValue(errors, `subRoutes.${index}`) ? 'error' : '')}>
                                        <IonCol size={disabled ? "12" : "10"}>
                                            <Controller
                                                name={getFieldNameWithPrefix(`subRoutes.${index}.startingPlace`, prefix)}
                                                control={control}
                                                defaultValue={subRoute.startingPlace}
                                                render={(props) => <input type="hidden" {...props} />}
                                            />
                                            <Controller
                                                name={getFieldNameWithPrefix(`subRoutes.${index}.startingPlaceCountry`, prefix)}
                                                control={control}
                                                defaultValue={subRoute.startingPlaceCountry}
                                                render={(props) => <input type="hidden" {...props} />}
                                            />
                                            <Controller
                                                name={getFieldNameWithPrefix(`subRoutes.${index}.destinationPlaceCountry`, prefix)}
                                                control={control}
                                                defaultValue={subRoute.destinationPlaceCountry}
                                                render={(props) => <input type="hidden" {...props} />}
                                            />
                                            <Controller
                                                name={getFieldNameWithPrefix(`subRoutes.${index}.distance`, prefix)}
                                                control={control}
                                                defaultValue={subRoute.distance}
                                                render={(props) => <input type="hidden" {...props} />}
                                                rules={{valueAsNumber: true}}
                                            />
                                            <Controller
                                                name={getFieldNameWithPrefix(`subRoutes.${index}.destinationPlace`, prefix)}
                                                control={control}
                                                defaultValue={subRoute.destinationPlace}
                                                rules={{required: true}}
                                                render={(field) => (
                                                    <CityAutocomplete
                                                        ref={field.ref}
                                                        onChangeCallback={(place: any) => {
                                                            field.onChange(place);

                                                            if (onRowChange) {
                                                                onRowChange();
                                                            }
                                                        }}
                                                        disabled={disabled}
                                                        setCountry={(country) => setValue(getFieldNameWithPrefix(`subRoutes.${index}.destinationPlaceCountry`, prefix), country)}
                                                        defaultValue={field.value || subRoute.destinationPlace}
                                                        style={{borderRadius: "12px"}}
                                                    />
                                                )}
                                            />
                                        </IonCol>
                                        {
                                            !disabled &&
                                            <IonCol size="2" className="label" onClick={() => handleSubRouteDelete(index)}>
                                                <StyledInputUnit icon={true}>
                                                    <BinIcon/>
                                                </StyledInputUnit>
                                            </IonCol>
                                        }
                                    </StyledInputGroup>
                                </div>
                            );
                        }

                        return (
                            <React.Fragment key={subRoute.id}>
                                <Controller
                                    name={getFieldNameWithPrefix(`subRoutes.${index}.startingPlace`, prefix)}
                                    control={control}
                                    defaultValue={subRoute.startingPlace}
                                    render={(props) => <input type="hidden" {...props} />}
                                />
                                <Controller
                                    name={getFieldNameWithPrefix(`subRoutes.${index}.startingPlaceCountry`, prefix)}
                                    control={control}
                                    defaultValue={subRoute.startingPlaceCountry}
                                    render={(props) => <input type="hidden" {...props} />}
                                />
                                <Controller
                                    name={getFieldNameWithPrefix(`subRoutes.${index}.destinationPlace`, prefix)}
                                    control={control}
                                    defaultValue={subRoute.destinationPlace}
                                    render={(props) => <input type="hidden" {...props} />}
                                />
                                <Controller
                                    name={getFieldNameWithPrefix(`subRoutes.${index}.destinationPlaceCountry`, prefix)}
                                    control={control}
                                    defaultValue={subRoute.destinationPlaceCountry}
                                    render={(props) => <input type="hidden" {...props} />}
                                />
                                <Controller
                                    name={getFieldNameWithPrefix(`subRoutes.${index}.distance`, prefix)}
                                    control={control}
                                    defaultValue={subRoute.distance}
                                    render={(props) => <input type="hidden" {...props} />}
                                    rules={{valueAsNumber: true}}
                                />
                            </React.Fragment>
                        );
                    });
                })()
            }
            {
                !disabled &&
                <IonRow>
                    <IonCol size="12">
                        <StyledButtonOption>
                            <div className="btn-option center" onClick={() => handleSubRouteAddNewRow()}>
                                <AddIcon/>
                                <span>{t('travelAllowance.rideRoute.addSubRoute')}</span>
                            </div>
                        </StyledButtonOption>
                    </IonCol>
                </IonRow>
            }
        </section>
    );
});

export default React.memo(SubRoutesFormField);
